define('home/views/common/FetchableCollectionView',[
    'underscore',
    'jquery',
    'backbone',
    'backbone-marionette',

    'core/stance/main',

    'home/views/FetchingView',
], function (
    _,
    $,
    Backbone,
    Marionette,

    stance,

    FetchingView
) {
    'use strict';

    /**
     * An extended Marionette Collection view that shows not only the empty view,
     * but also a fetching view and an error view. If any of these views are not
     * given, it will use defaults. If the intention is to show nothing for one
     * of these states, override the default view with a blank view. Defaults
     * explained below.
     *
     * Any given views will be passed this view's model as a model, should this
     * view have a model.
     *
     * In order to determine the state of the collection (fetching/error), the view
     * will first check the view's model's attributes, if a model was given. If
     * a model was not given, it will check the fetching/error property or function
     * on the view's collection.
     *
     * fetchingView - if given, will be shown when fetching is true and the collection
     *                is empty. (don't want to hide existing collection items if more
     *                are being fetched)
     *                default: full page centered spinner
     * emptyView - if given, will be shown when the collection is empty and both fetching
     *             and error are false.
     *             default: blank
     */
    var FetchableCollectionView = Marionette.CollectionView.extend({
        fetchingView: FetchingView,

        // If emptyView is not specified, show a view that renders nothing rather than
        // not specifying an emptyView at all. Looks the same to the user, but this way
        // _showingEmptyView will be set to true when empty.
        emptyView: Backbone.View,

        initialize: function (options) {
            this.listenable = this.model || this.collection;
            this.listenTo(this.listenable, 'change:fetching', this.render);
            this.listenTo(this.collection, 'add', this.closeNonItemViews);
            this.listenTo(this.collection, 'reset', this.closeViews);
            this.listenTo(this, 'before:item:added', this.onFirstItem);

            if (options.infiniteScroll)
                this.setupInfiniteScroll();

            // Only trigger the renderInitialContent event once
            this.triggerRenderInitialContent = _.once(_.bind(this.trigger, this, 'renderInitialContent'));
        },

        setupInfiniteScroll: function () {
            stance.bindWindowEvents(); // noops after first call
            this.listenTo(this, 'after:item:added', _.debounce(this.updateInfiniteScrollTrigger, 0));
        },

        // Performance: track the time to show the first card on the DOM
        onFirstItem: function (view) {

            // Don't count fetching views, error views, empty views, etc.
            // (Specifically not using isShowingNonEmptyView because
            // this is more explicit.)
            if (this.collection.at(0) !== view.model)
                return;

            this.stopListening(this, 'before:item:added', this.onFirstItem);

            // Note: 'before:item:added' is fired before 'show'
            // See Marionette.CollectionView.prototype.addItemView
            this.listenToOnce(view, 'show', function () {
                this.trigger('showFirstItem', view);
            });
        },

        infiniteScrollTriggerOffset: function () {
            return -$(window).height();
        },

        updateInfiniteScrollTrigger: function () {
            // Don't set up inifinite scroll trigger if the view
            // has no items.
            if (!this.collection.length)
                return;

            var views = _.values(this.children._views);

            if (!views.length)
                return;

            var lastVisibleViewIndex = _.findLastIndex(views, function (view) {
                return !/(\s|^)hidden(\s|$)/.test(view.$el.attr('class'));
            });

            if (lastVisibleViewIndex < 0)
                return;

            var view = views[lastVisibleViewIndex];

            if (!view)
                return;

            var infiniteScrollTrigger = this.infiniteScrollTrigger;

            // Since this function can be triggered by adding a single
            // item to the collection, be sure to clean up the stance object
            // that was previously bound (don't want to fire multiple 'enter' events
            // at the same time)
            if (infiniteScrollTrigger)
                this.stopListening(infiniteScrollTrigger);

            infiniteScrollTrigger = stance({
                el: view.el,
                topEdgeOffset: this.infiniteScrollTriggerOffset,
            });
            this.infiniteScrollTrigger = infiniteScrollTrigger;

            this.listenToOnce(infiniteScrollTrigger, 'enter', this.loadMore);
        },

        loadMore: function () {
            var listenable = this.listenable;
            if (listenable.fetchItems)
                listenable.fetchItems();
            else if (listenable.more)
                listenable.more();

            this.infiniteScrollTrigger = null;
        },

        // Hack to fix bad Marionette behaviour where the empty view is shown briefly
        // while closing this CollectionView. Must set _showingEmptyView back to false
        // after close.
        onCollectionBeforeClose: function () {
            this._showingEmptyView = true;
        },
        onCollectionClosed: function () {
            this._showingEmptyView = false;
        },

        render: function () {
            var isEmpty = !this.collection.length;
            var isFetching = this.isFetching();
            var isError = this.isError();

            if (isEmpty) {
                if (isFetching) {
                    this.showFetchingView();

                } else if (isError) {
                    // Show nothing on initial fetch error. Footer view will show the
                    // error message.
                    this.closeViews();

                    // The function will only trigger the event once.
                    this.triggerRenderInitialContent();

                } else if (!this._showingEmptyView) {
                    Marionette.CollectionView.prototype.render.apply(this, arguments);

                    // The function will only trigger the event once.
                    this.triggerRenderInitialContent();
                }
            } else if (!this.children.length || this.isShowingNonItemView()) {
                // Only rerender the page if it's not already showing the
                // default marionette collection view because rerendering would
                // cause the page to scroll back up to the top
                // and show the same content as before.
                // Do this even if there's an error. Error message will show below any
                // previously fetched content in the footer view.
                this.closeViews();
                Marionette.CollectionView.prototype.render.apply(this, arguments);

                // The function will only trigger the event once.
                this.triggerRenderInitialContent();
            }
        },

        buildItemView: function (item, ItemViewType, itemViewOptions) {
            var emptyViewOptions = Marionette.getOption(this, 'emptyViewOptions');
            if (this._showingEmptyView && emptyViewOptions) {
                itemViewOptions = emptyViewOptions;
                if (_.isFunction(itemViewOptions))
                    itemViewOptions = itemViewOptions.call(this);
            }

            return Marionette.CollectionView.prototype.buildItemView.call(this, item, ItemViewType, itemViewOptions);
        },

        /**
         * Required to show items in their sorted order, for a sorted collection.
         * This is the default behaviour in Marionette 2.0+.
         *
         * Copied from
         * https://github.com/marionettejs/backbone.marionette/wiki/Adding-support-for-sorted-collections
         */
        appendHtml: function (collectionView, itemView, index) {
            if (collectionView.isBuffering)
                collectionView._bufferedChildren.push(itemView);

            var childrenContainer = collectionView.isBuffering ?
                $(collectionView.elBuffer) :
                collectionView.$el;

            var children = childrenContainer.children();
            if (children.size() <= index)
                childrenContainer.append(itemView.el);
            else
                children.eq(index).before(itemView.el);
        },

        isFetching: function () {
            return this.checkState('fetching');
        },

        isError: function () {
            return this.checkState('error');
        },

        checkState: function (state) {
            // If this.listenable is not defined, the child class probably forgot
            // to call the parent's initialize method in the overridden initialize
            return this.listenable.get ?
                this.listenable.get(state) :
                _.result(this.listenable, state);
        },

        /**
         * Special views (fetching/error) may be closed from marionette's
         * collection view calling closeChildren. When this happens, need
         * to update the state which tracks if any of the special views
         * are currently showing.
         */
        bindViewClose: function (view, propertyName) {
            var self = this;

            // Must use view.on, not this.listenTo, because this.stopListening will
            // be called for view before close is triggered on view, so the event
            // will never fire.
            view.on('close', function () {
                self[propertyName] = false;
            });
        },

        /**
         * Closes all the special case views: fetching/error/empty.
         * This occurs when the collection is updated. Marionette's
         * collection view will handle showing/removing item views,
         * this just ensures that none of the special views are showing
         * at the same time as item views.
         */
        closeNonItemViews: function () {
            if (this.isShowingNonItemView())
                this.closeViews();
        },

        isShowingNonItemView: function () {
            return this._showingFetchingView ||
                this._showingEmptyView;
        },

        /**
         * Closes all views, the special ones (fetching/error/empty),
         * and also the item views, should they be showing. This occurs
         * when a new special view is shown.
         */
        closeViews: function () {
            // Hack to ensure that this.closeChildren() does not trigger
            // the empty view to show. Must set to null immediately after
            // calling this.closeChildren()
            this._showingEmptyView = true;

            this.closeChildren();

            this._showingEmptyView = false;
            this._showingFetchingView = false;
        },

        showFetchingView: function () {
            var FetchingView = Marionette.getOption(this, 'fetchingView');

            if (FetchingView && !this._showingFetchingView) {
                this.closeViews();
                this._showingFetchingView = true;
                var model = this.model || new Backbone.Model();
                var fetchingView = this.addItemView(model, FetchingView, 0);
                this.bindViewClose(fetchingView, '_showingFetchingView');
            }
        },
    });

    return FetchableCollectionView;
});

